'use client';

import React, { useCallback, useState } from 'react';
import ZeroFeesTag from 'src/components/Tags/ZeroFeesTag';
import { cj } from 'src/utils/classNames';
import useCloseBottomDrawer from 'src/components/Drawers/BaseDrawers/BottomDrawer/hooks/useCloseBottomDrawer';
import GridSectionWrapper from 'src/components/GridSections/GridSectionWrapper/GridSectionWrapper';
import Chevron from 'src/components/Icons/Chevron';
import MarketSelectorDropdown from 'src/components/MarketSelector/MarketSelectorDropdown';
import MarketStatsBar from 'src/components/MarketStatsBar/MarketStatsBar';
import MarketIcon from 'src/components/Utils/MarketIcon';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';
import useTargetedPopover from 'src/hooks/useTargetedPopover';
import useVelocityStore from 'src/stores/VelocityStore/useVelocityStore';
import { ENUM_UTILS } from '@velocity-exchange/common';
import { OracleSource } from '@velocity-exchange/sdk';
import MarketSelectorPriceDisplay from 'src/components/MarketSelector/MarketSelectorPriceDisplay';
import Tooltip from 'src/components/primitives/Tooltip/Tooltip';

/**
 * Displays the clickable market selector, which opens a dropdown on desktop and a bottom drawer on mobile.
 */
export const MarketSelectorDisplay = () => {
	const currentlySelectedMarketId = useVelocityStore(
		(s) => s.selectedMarket.marketId
	);
	const currentlySelectedMarketSymbol = useVelocityStore(
		(s) => s.selectedMarket.current?.market.symbol
	);
	const currentlySelectedMarketName = useVelocityStore(
		(s) => s.selectedMarket.current?.uiSymbols.marketDisplaySymbol
	);
	const currentlySelectedMarketOracleSource = useVelocityStore(
		(s) => s.selectedMarket.current?.market.oracleSource
	);

	const isPerp = currentlySelectedMarketId.isPerp;

	const isMobileScreenSize = useIsMobileScreenSize();
	const [isDrawerOpen, setIsDrawerOpen] = useState(false);

	const {
		refs,
		floatingStyles,
		getReferenceProps,
		getFloatingProps,
		setIsPopoverOpen: setShowMarketSelectorDropdown,
		isPopoverOpen: showMarketSelectorDropdown,
	} = useTargetedPopover(
		{
			strategy: 'fixed',
			placement: 'bottom-start',
		},
		{
			offset: 4,
			disableAutoPlacement: true,
		}
	);

	const handleClose = useCallback(() => {
		if (isMobileScreenSize) {
			setIsDrawerOpen(false);
		} else {
			setShowMarketSelectorDropdown(false);
		}
	}, [isMobileScreenSize, setIsDrawerOpen, setShowMarketSelectorDropdown]);

	const handleOpenChange = useCallback(() => {
		if (isMobileScreenSize) {
			setIsDrawerOpen(!isDrawerOpen);
		} else {
			setShowMarketSelectorDropdown(!showMarketSelectorDropdown);
		}
	}, [
		isMobileScreenSize,
		isDrawerOpen,
		setShowMarketSelectorDropdown,
		showMarketSelectorDropdown,
	]);

	const { closeDrawer, isDrawerClosing } = useCloseBottomDrawer({
		onClose: () => setIsDrawerOpen(false),
	});

	return (
		<GridSectionWrapper
			noPad
			className={cj(
				'flex min-w-max shrink-0 flex-col justify-center rounded-none rounded-l-xs border-0',
				isMobileScreenSize ? 'w-full' : ''
			)}
		>
			<button
				ref={refs.setReference}
				{...getReferenceProps()}
				id="marketSelector"
				className={cj(
					'z-40 flex h-full w-full flex-row items-center justify-between bg-container-bg text-text-default',
					'group relative hover:cursor-pointer',
					!isMobileScreenSize && 'p-2 hover:text-text-secondary'
				)}
				onClick={handleOpenChange}
				data-testid="market-selector"
			>
				<div className="flex w-full flex-row items-center justify-between px-3 sm:gap-x-2 sm:px-2 sm:pr-0">
					<div
						className={cj(
							'flex items-center gap-x-1 sm:gap-x-2',
							// Max width here allows long market names to truncate.
							isMobileScreenSize && 'max-w-[180px]'
						)}
					>
						<MarketIcon
							marketSymbol={currentlySelectedMarketSymbol ?? ''}
							className="mb-0.5"
							isSpot={!isPerp}
							customHeight={24}
							customWidth={24}
							sizeClass="size-2xl"
						/>

						{ENUM_UTILS.match(
							currentlySelectedMarketOracleSource,
							OracleSource.PYTH_1K
						) ||
						ENUM_UTILS.match(
							currentlySelectedMarketOracleSource,
							OracleSource.PYTH_1M
						) ? (
							<Tooltip
								decoration="none"
								content={
									<span>{`This perp represents the price of ${currentlySelectedMarketSymbol} * ${
										ENUM_UTILS.match(
											currentlySelectedMarketOracleSource,
											OracleSource.PYTH_1M
										)
											? '1 million'
											: '1 thousand'
									}`}</span>
								}
							>
								<MarketName marketName={currentlySelectedMarketName ?? ''} />
							</Tooltip>
						) : (
							<MarketName marketName={currentlySelectedMarketName ?? ''} />
						)}

						<ZeroFeesTag
							marketIndex={currentlySelectedMarketId.marketIndex}
							isPerp={currentlySelectedMarketId.isPerp}
							className="py-[3px]"
						/>

						{isMobileScreenSize && (
							<SelectorChevron
								showMarketSelectorDropdown={showMarketSelectorDropdown}
							/>
						)}
					</div>

					{!isMobileScreenSize && (
						<SelectorChevron
							showMarketSelectorDropdown={showMarketSelectorDropdown}
						/>
					)}

					{isMobileScreenSize && <MarketSelectorPriceDisplay />}
				</div>
			</button>
			{(showMarketSelectorDropdown || isDrawerOpen) && (
				<MarketSelectorDropdown
					onClose={handleClose}
					marketType={currentlySelectedMarketId.marketType}
					setFloating={refs.setFloating}
					floatingStyles={floatingStyles}
					getFloatingProps={getFloatingProps}
					closeDrawer={closeDrawer}
					isDrawerClosing={isDrawerClosing}
				/>
			)}
		</GridSectionWrapper>
	);
};

const SelectorChevron = ({
	showMarketSelectorDropdown,
	className,
}: {
	showMarketSelectorDropdown: boolean;
	className?: string;
}) => {
	return (
		<Chevron
			direction={showMarketSelectorDropdown ? 'up' : 'down'}
			className={className}
			size={20}
		/>
	);
};

const MarketName = ({ marketName }: { marketName: string }) => {
	const isMobileScreenSize = useIsMobileScreenSize();
	return (
		<span
			className={cj(
				'typo-label-md whitespace-nowrap md:typo-label-lg',
				isMobileScreenSize && 'truncate'
			)}
		>
			{marketName}
		</span>
	);
};

const MarketSelector = () => {
	return (
		<div className="inline-flex thin-scroll h-full w-full items-center justify-center overflow-hidden rounded-md bg-container-bg">
			<MarketSelectorDisplay />
			<MarketStatsBar />
		</div>
	);
};

export default MarketSelector;
